import API from '../../utils/api';
import * as types from './types';
import { setError } from './errors';
import { ENDPOINT_URL, EVENTS_PAGE_LIMIT, internalError } from '../../constants/defaultValues';
import {
    EVENTS_CHANGED,
    EVENTS_FETCHING_CHANGED,
    EVENTS_FILTER_CHANGED,
    EVENTS_FILTERS_CHANGED,
    EVENTS_PAGE_OFFSET_CHANGED,
    EVENTS_PAGINATED,
    EVENTS_PAGINATING_CHANGED,
    POPULAR_EVENTS_CHANGED,
} from './types';
import { useLatestRequest } from '../../utils/network';
import {
    EVENT_FILTER_OPTIONS,
    getInitialEventFilters,
    getSavedEventFilters,
    mergeEventFilters,
} from '../../constants/event-filters';
import { generateQuery } from '../../utils/helpers';
import BaseModel from '../../models/BaseModel';
import EventWithSeasonModel from '../../models/agent/event-with-season/event-with-season-model';
import EventWithoutSeasonModel from '../../models/agent/event-without-season/event-without-season-model';
import StaticContentModel from '../../models/agent/static-content/static-content-model';
import { EVENT_TYPE_ENUM } from '../../constants/enums';

export const setSportRefId = sportRefId => ({
    type: types.SET_SPORT_REF_ID,
    payload: sportRefId,
});

export const changeEventFilters = ({ filters, shouldUpdateEvents = true } = {}) => ({
    type: EVENTS_FILTERS_CHANGED,
    payload: { filters, shouldUpdateEvents },
});

export const changeEventFilter = ({ key, value }) => ({
    type: EVENTS_FILTER_CHANGED,
    payload: { key, value },
});

export const setFiltersFetching = isFetching => ({
    type: types.EVENTS_FILTERS_FETCHING_CHANGED,
    payload: isFetching,
});

export const getEvents =
    (query = {}, { showLoading = true } = {}) =>
    async (dispatch, getStore) => {
        try {
            const store = getStore();
            const { sportRefId, isLive, filters } = store.sportEvents;
            const { pageOffset } = query;

            if (showLoading && pageOffset === 0) {
                dispatch({
                    type: EVENTS_FETCHING_CHANGED,
                    payload: true,
                });
            } else if (pageOffset > 0) {
                dispatch({
                    type: EVENTS_PAGINATING_CHANGED,
                    payload: true,
                });
            }

            const queryParams = generateQuery(filters);
            const params = {
                live: isLive,
                sportTypeId: sportRefId,
                pageOffset,
                pageLimit: EVENTS_PAGE_LIMIT,
                ...queryParams,
                ...query,
            };

            const { sortId } = queryParams;

            const res = await useLatestRequest('/get-events', async () => {
                return API.get(`${ENDPOINT_URL}/events`, {
                    params,
                });
            });

            if (!res) return;

            const { info, item } = res.data;

            if (info.success) {
                const { events, count: total } =
                    sortId === EVENT_FILTER_OPTIONS.orders.byLeague.id
                        ? BaseModel.construct(EventWithSeasonModel, item)
                        : BaseModel.construct(EventWithoutSeasonModel, item);

                const hasNextPage =
                    sortId === EVENT_FILTER_OPTIONS.orders.byLeague.id
                        ? events.flatMap(s => s.events).length >= EVENTS_PAGE_LIMIT
                        : events.length >= EVENTS_PAGE_LIMIT;

                const actionType = pageOffset > 0 ? EVENTS_PAGINATED : EVENTS_CHANGED;
                dispatch({
                    type: actionType,
                    payload: {
                        events,
                        sortType: sortId,
                        isFetching: false,
                        isPaginating: false,
                        total,
                        hasNextPage,
                        pageOffset: pageOffset + EVENTS_PAGE_LIMIT,
                    },
                });
            }

            return res;
        } catch (error) {
            if (error.message.cancelled) return;
            console.log({ error });
            let errors;

            if (error?.response?.data?.info?.errors) {
                errors = error.response.data.info.errors;
                dispatch(setError(errors[0]));
            }

            dispatch({
                type: EVENTS_CHANGED,
                payload: {
                    isFetching: false,
                },
            });
        }
    };

export const getPopularEvents = () => async (dispatch, getStore) => {
    try {
        const store = getStore();
        const { sportRefId, isLive } = store.sportEvents;

        if (isLive || !sportRefId) return;

        const params = {
            sportTypeId: sportRefId,
        };

        const res = await useLatestRequest('/get-popular-events', async () => {
            return API.get(`${ENDPOINT_URL}/events/popular-events`, {
                params,
            });
        });

        if (!res) return;

        const { info, item } = res.data;

        if (info.success) {
            const { events } = BaseModel.construct(EventWithoutSeasonModel, item);

            dispatch(setPopularEvents(events));
        }

        return res;
    } catch (error) {
        if (error.message.cancelled) return;
        console.log({ error });
    }
};

export const setPopularEvents = popularEvents => ({
    type: POPULAR_EVENTS_CHANGED,
    payload: { popularEvents },
});

export const clearPopularEvents = () => ({
    type: POPULAR_EVENTS_CHANGED,
    payload: { popularEvents: [] },
});

export const changeEventsPageOffset = offset => ({
    type: EVENTS_PAGE_OFFSET_CHANGED,
    payload: offset,
});

export const getSportEventsByName = name => async dispatch => {
    dispatch({
        type: types.GET_EVENTS_BY_NAME_REQUEST,
    });

    await API.get(`${ENDPOINT_URL}/events/search?eventInfo=${name}`)
        .then(res => {
            const { info, items } = res.data;

            if (info.success) {
                dispatch({
                    type: types.GET_EVENTS_BY_NAME_SUCCESS,
                    payload: items,
                });
            }
        })
        .catch(err => {
            const errors = err?.response?.data?.info?.errors;
            if (!errors) {
                dispatch(setError(internalError));
                return false;
            }
            dispatch(setError(errors[0]));
        });
};

export const clearEventResult = () => dispatch => {
    dispatch({
        type: types.CLEAR_EVENT_RESULT,
    });
};

export const getStaticData = query => async dispatch => {
    const { isESports, shouldUpdateEvents = true, ...params } = query;

    try {
        dispatch(setFiltersFetching(true));

        const response = await useLatestRequest('/get-static', async () => {
            return API.get(`${ENDPOINT_URL}/static`, {
                params,
            });
        });

        if (!response) return;

        const { info, item } = response.data;

        if (info.success) {
            const staticContent = BaseModel.construct(StaticContentModel, item);

            dispatch({
                type: types.GET_MAIN_MARKETS_SUCCESS,
                payload: staticContent.description.mainMarkets,
            });

            dispatch({
                type: types.GET_MARKET_DESCS_SUCCESS,
                payload: {
                    markets: staticContent.description.markets,
                    outcomes: staticContent.description.outcomes,
                    specifiers: staticContent.description.specifiers,
                },
            });

            const isOutrights = query.eventType === EVENT_TYPE_ENUM.outright;

            const currentFilters = getInitialEventFilters(query.live, isESports, isOutrights);
            const newFilters = staticContent.header;
            const savedFilters = getSavedEventFilters({
                sportRefId: query.sportTypeId,
                isLive: query.live,
                isOutrights,
            });

            const mergedFilters = mergeEventFilters(currentFilters, newFilters, savedFilters);

            dispatch(changeEventFilters({ filters: mergedFilters, shouldUpdateEvents }));
        } else {
            dispatch(setFiltersFetching(false));
        }
    } catch (error) {
        console.log(error);
        let errors;

        if (error?.response?.data?.info?.errors) {
            errors = error.response.data.info.errors;
            dispatch(setError(errors[0]));
        }

        dispatch(setFiltersFetching(false));
    }
};

export const setEvents = payload => ({
    type: EVENTS_CHANGED,
    payload,
});

export const clearEvents = () => ({
    type: EVENTS_CHANGED,
    payload: {
        events: [],
        total: 0,
    },
});

export const setCompetitionType = isLive => ({
    type: types.SET_SPORTS_COMPETITION_TYPE,

    payload: isLive,
});

export const setESportsType = isESports => ({
    type: types.SET_SPORTS_ESPORTS_TYPE,
    payload: isESports,
});

export const setEventType = eventType => ({
    type: types.SET_SPORTS_EVENT_TYPE,
    payload: eventType,
});
